import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiPlus, FiTrash2, FiFileText, FiEdit, FiUserPlus, FiDollarSign, FiTool, FiPrinter, FiSearch, FiCalendar, FiFilter, FiPieChart, FiBarChart2, FiTrendingUp } from 'react-icons/fi';
import { FaGasPump, FaWrench, FaTools, FaCarCrash, FaFileInvoiceDollar, FaShieldAlt, FaQuestionCircle, FaFilter as FaFilterIcon, FaBolt, FaTruck } from 'react-icons/fa';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { API_BASE_URL } from '../../../config';
import styles from './DespesasPopup.module.css';
import EditDespesasPopup from './EditDespesaPopup';
import FornecedorPopup from '../Manutencao/FornecedorPopup';
import { gerarRelatorioDeSelecaoPDF } from './geradorRelatorioDespesas';
import './sweetalert-custom.css';
import logo from '../../../img/logo rio verde pdf.png';

ChartJS.register(ArcElement, Tooltip, Legend, Title, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const ITEMS_PER_PAGE = 10;

const parseBRLNumber = (value) => {
    if (typeof value !== 'string' || !value) return 0;
    return parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

const formatBRLNumber = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return '';
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(number);
};

const formatHighPrecisionCurrency = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return "R$ 0,00";
    
    const formattedNumber = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(number);

    return `R$ ${formattedNumber}`;
};

const robustParseFloat = (value) => {
    if (value === null || value === undefined) return 0;
    return parseFloat(String(value).replace(',', '.')) || 0;
};

const iconMap = {
    'Combustível': <FaGasPump title="Combustível" />,
    'Manutenção Corretiva': <FaWrench title="Manutenção Corretiva" />,
    'Manutenção Preventiva': <FaTools title="Manutenção Preventiva" />,
    'Lubrificantes/Filtros': <FaFilterIcon title="Lubrificantes/Filtros" />,
    'Pneus': <FaCarCrash title="Pneus" />,
    'Documentação': <FaFileInvoiceDollar title="Documentação" />,
    'Seguro': <FaShieldAlt title="Seguro" />,
    'Manutenção Elétrica': <FaBolt title="Manutenção Elétrica" />,
    'Frete': <FaTruck title="Frete" />,
    'Outros': <FaQuestionCircle title="Outros" />,
};

const DespesasPopup = ({ vehicle, onClose }) => {
    const [despesas, setDespesas] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [tiposDespesa, setTiposDespesa] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFornecedorPopupOpen, setIsFornecedorPopupOpen] = useState(false);
    const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
    const [selectedDespesa, setSelectedDespesa] = useState(null);
    const [selecaoRelatorio, setSelecaoRelatorio] = useState([]);
    const chartContainerRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showOdometro, setShowOdometro] = useState(false);
    const [chartType, setChartType] = useState('doughnut');

    const initialState = {
        data: new Date().toISOString().slice(0, 10),
        tipo_despesa: '',
        descricao: '',
        odometro: '',
        fornecedor_id: '',
        numero_nota_fiscal: '',
        quantidade_itens: '1,00',
        valor_unitario: '',
    };
    const [novaDespesa, setNovaDespesa] = useState(initialState);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };
            const [despesasRes, fornecedoresRes, tiposRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/veiculos/${vehicle.id}/despesas`, { headers }),
                axios.get(`${API_BASE_URL}/api/fornecedores`, { headers }),
                axios.get(`${API_BASE_URL}/api/tipos-despesa`, { headers })
            ]);
            setDespesas(despesasRes.data);
            setFornecedores(fornecedoresRes.data);
            setTiposDespesa(tiposRes.data);
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            Swal.fire({ icon: 'error', title: 'Erro!', text: 'Não foi possível carregar os dados.' });
        } finally {
            setIsLoading(false);
        }
    }, [vehicle.id]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const processedDespesas = useMemo(() => {
        if (!despesas || despesas.length === 0) return [];

        const fuelEntries = despesas
            .filter(d => d.tipo_despesa === 'Combustível' && robustParseFloat(d.odometro) > 0)
            .sort((a, b) => robustParseFloat(a.odometro) - robustParseFloat(b.odometro));

        const calculosMap = new Map();

        for (let i = 1; i < fuelEntries.length; i++) {
            const prevEntry = fuelEntries[i - 1];
            const currEntry = fuelEntries[i];

            const prevOdometro = robustParseFloat(prevEntry.odometro);
            const currOdometro = robustParseFloat(currEntry.odometro);
            
            const km_percorrido = currOdometro - prevOdometro;
            const quantidade_itens = robustParseFloat(currEntry.quantidade_itens);

            if (km_percorrido > 0 && quantidade_itens > 0) {
                const consumo_km_l = km_percorrido / quantidade_itens;
                calculosMap.set(currEntry.id, { km_percorrido, consumo_km_l });
            } else {
                calculosMap.set(currEntry.id, { km_percorrido: 0, consumo_km_l: 0 });
            }
        }

        return despesas.map(despesa => {
            if (calculosMap.has(despesa.id)) {
                return {
                    ...despesa,
                    ...calculosMap.get(despesa.id)
                };
            }
            return {
                ...despesa,
                km_percorrido: robustParseFloat(despesa.km_percorrido),
                consumo_km_l: robustParseFloat(despesa.consumo_km_l)
            };
        });
    }, [despesas]);

    const filteredDespesas = useMemo(() => {
        return processedDespesas.filter(d => {
            const itemDate = new Date(d.data);
            const startDate = filterStartDate ? new Date(filterStartDate) : null;
            const endDate = filterEndDate ? new Date(filterEndDate) : null;
            
            if (endDate) { 
                endDate.setDate(endDate.getDate() + 1);
            }

            const normalizedItemDate = new Date(itemDate.getUTCFullYear(), itemDate.getUTCMonth(), itemDate.getUTCDate());

            if (startDate && normalizedItemDate < startDate) {
                return false;
            }
            if (endDate && normalizedItemDate >= endDate) {
                return false;
            }
            if (filterType && d.tipo_despesa !== filterType) {
                return false;
            }
            if (searchTerm && !d.descricao.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }
            return true;
        });
    }, [processedDespesas, searchTerm, filterType, filterStartDate, filterEndDate]);
    
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterType, filterStartDate, filterEndDate]);

    const { pages, totalPages } = useMemo(() => {
        const itemsWithHeaders = [];
        let lastMonth = null;
        
        filteredDespesas.forEach(d => {
            const itemMonth = new Date(d.data).toLocaleString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
            if (itemMonth !== lastMonth) {
                itemsWithHeaders.push({ type: 'header', id: itemMonth, title: itemMonth.charAt(0).toUpperCase() + itemMonth.slice(1) });
                lastMonth = itemMonth;
            }
            itemsWithHeaders.push({ type: 'despesa', ...d });
        });

        if (itemsWithHeaders.length === 0) {
            return { pages: [], totalPages: 1 };
        }
        
        const allPages = [];
        let currentPageItems = [];
        for (const item of itemsWithHeaders) {
            currentPageItems.push(item);
            if (currentPageItems.length >= ITEMS_PER_PAGE) {
                const lastItem = currentPageItems[currentPageItems.length - 1];
                if (lastItem.type === 'header' && currentPageItems.length > 1) {
                    const orphanHeader = currentPageItems.pop();
                    allPages.push(currentPageItems);
                    currentPageItems = [orphanHeader];
                } else {
                    allPages.push(currentPageItems);
                    currentPageItems = [];
                }
            }
        }
        
        if (currentPageItems.length > 0) {
            allPages.push(currentPageItems);
        }
        
        return { pages: allPages, totalPages: allPages.length };
    }, [filteredDespesas]);

    const itemsDaPaginaAtual = pages[currentPage - 1] || [];

    const valorTotalCalculado = useMemo(() => {
        const qtd = parseBRLNumber(novaDespesa.quantidade_itens);
        const vlrUnit = parseBRLNumber(novaDespesa.valor_unitario);
        return qtd * vlrUnit;
    }, [novaDespesa.quantidade_itens, novaDespesa.valor_unitario]);
    
    const chartData = useMemo(() => {
        const sourceData = filteredDespesas.length > 0 ? filteredDespesas : processedDespesas;
        if (!sourceData || sourceData.length === 0) return null;
        
        const totaisPorTipo = sourceData.reduce((acc, despesa) => {
            const tipo = despesa.tipo_despesa;
            const valor = robustParseFloat(despesa.valor);
            if (!acc[tipo]) {
                acc[tipo] = 0;
            }
            acc[tipo] += valor;
            return acc;
        }, {});

        const sortedData = Object.entries(totaisPorTipo).sort(([, a], [, b]) => b - a);
        const labels = sortedData.map(([tipo]) => tipo);
        const data = sortedData.map(([, total]) => total);
        
        const softPalette = ['#5B7A9D', '#6AB187', '#FACF5A', '#F78E69', '#A87B9A', '#77C2A7', '#F4A66E', '#D296B6'];
        const hoverPalette = ['#4A6382', '#579E73', '#F9C23C', '#F57A52', '#936884', '#63B597', '#F2985A', '#C883A7'];

        return {
            labels,
            datasets: [{
                label: 'Total Gasto',
                data,
                backgroundColor: softPalette,
                hoverBackgroundColor: hoverPalette,
                borderColor: hoverPalette,
                borderWidth: 1,
            }]
        };
    }, [processedDespesas, filteredDespesas]);
    
    const kpiData = useMemo(() => {
        const sourceData = filteredDespesas;
        if (!sourceData || sourceData.length === 0) {
            return { custoTotal: 0, principalDespesa: 'N/A', consumoMedio: 0 };
        }
        const custoTotal = sourceData.reduce((sum, d) => sum + robustParseFloat(d.valor), 0);
        const principalDespesa = chartData && chartData.labels.length > 0 ? chartData.labels[0] : 'N/A';
        
        const despesasCombustivelComConsumo = sourceData.filter(d => 
            d.tipo_despesa === 'Combustível' && 
            d.km_percorrido > 0 &&
            robustParseFloat(d.quantidade_itens) > 0
        );
        
        const totalKmPercorrido = despesasCombustivelComConsumo.reduce((sum, d) => sum + robustParseFloat(d.km_percorrido), 0);
        const totalLitros = despesasCombustivelComConsumo.reduce((sum, d) => sum + robustParseFloat(d.quantidade_itens), 0);
        const consumoMedio = totalLitros > 0 ? totalKmPercorrido / totalLitros : 0;

        return { custoTotal, principalDespesa, consumoMedio };
    }, [filteredDespesas, chartData]);

    const handleNumericInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value.replace(/[^0-9,]/g, '');
        setNovaDespesa(prev => ({ ...prev, [name]: formattedValue }));
    };

    const handleAddNewTipoDespesa = async () => {
        const { value: novoNome } = await Swal.fire({
            title: 'Adicionar Novo Tipo',
            input: 'text',
            inputLabel: 'Nome do novo tipo de despesa',
            inputPlaceholder: 'Ex: Pedágio, Multa, etc...',
            showCancelButton: true,
            confirmButtonText: 'Salvar',
            cancelButtonText: 'Cancelar',
            customClass: {
                container: 'swal-container-custom',
                popup: 'swal-popup-custom',
                title: 'swal-title-custom',
                confirmButton: 'swal-confirm-button-custom',
                cancelButton: 'swal-cancel-button-custom'
            },
            inputValidator: (value) => {
                if (!value) {
                    return 'Você precisa digitar um nome!';
                }
                if (tiposDespesa.some(t => t.nome.toLowerCase() === value.toLowerCase())) {
                    return 'Este tipo de despesa já existe!';
                }
            }
        });

        if (novoNome && novoNome.trim() !== '') {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.post(`${API_BASE_URL}/api/tipos-despesa`, 
                    { nome: novoNome },
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                
                const novoTipo = response.data;
                
                setTiposDespesa(prev => [...prev, novoTipo].sort((a, b) => a.nome.localeCompare(b.nome)));
                
                Swal.fire({
                    title: 'Sucesso!', 
                    text: 'Novo tipo salvo.', 
                    icon: 'success',
                    customClass: {
                        container: 'swal-container-custom',
                        popup: 'swal-popup-custom',
                        title: 'swal-title-custom',
                        confirmButton: 'swal-confirm-button-custom',
                    }
                });
                return novoTipo;
                
            } catch (error) {
                console.error("Erro ao criar tipo de despesa:", error);
                Swal.fire('Erro!', 'Não foi possível salvar o novo tipo.', 'error');
                return null;
            }
        }
        return null;
    };
    
    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        if (name === 'tipo_despesa' && value === '--CRIAR-NOVO--') {
            const novoTipo = await handleAddNewTipoDespesa();
            if (novoTipo) {
                setNovaDespesa(prev => ({ ...prev, tipo_despesa: novoTipo.nome })); 
            } else {
                setNovaDespesa(prev => ({ ...prev, tipo_despesa: '' }));
            }
        } else {
            setNovaDespesa(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleOdometroCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        setShowOdometro(isChecked);
        if (!isChecked) {
            setNovaDespesa(prev => ({ ...prev, odometro: '' }));
        }
    };
    
    const handleAddDespesa = async (e) => {
        e.preventDefault();
        const { data, tipo_despesa, descricao, valor_unitario, quantidade_itens } = novaDespesa;
        if (!data || !tipo_despesa || !descricao || !valor_unitario || !quantidade_itens) {
            Swal.fire({ 
                icon: 'warning', 
                title: 'Atenção', 
                text: 'Por favor, preencha todos os campos obrigatórios.' 
            });
            return;
        }
        
        const despesaParaEnviar = { 
            ...novaDespesa, 
            valor_unitario: parseBRLNumber(novaDespesa.valor_unitario), 
            quantidade_itens: parseBRLNumber(novaDespesa.quantidade_itens),
            odometro: novaDespesa.odometro ? parseBRLNumber(novaDespesa.odometro) : null
        };

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/veiculos/${vehicle.id}/despesas`, despesaParaEnviar, { 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            
            Swal.fire({ 
                icon: 'success', 
                title: 'Sucesso!', 
                text: 'Despesa adicionada com sucesso.', 
                confirmButtonText: 'OK', 
                customClass: { 
                    container: 'swal-container-custom', 
                    popup: 'swal-popup-custom', 
                    title: 'swal-title-custom', 
                    confirmButton: 'swal-confirm-button-custom' 
                } 
            });
            
            setNovaDespesa(initialState);
            setShowOdometro(false);
            fetchAllData();
        } catch (error) {
            console.error("Erro ao adicionar despesa:", error);
            Swal.fire({ 
                icon: 'error', 
                title: 'Erro!', 
                text: 'Não foi possível adicionar a despesa.' 
            });
        }
    };
    
    const handleDeleteDespesa = (despesaId) => {
        Swal.fire({
            title: 'Tem certeza?',
            text: "Você não poderá reverter esta ação!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar',
            customClass: { 
                container: 'swal-container-custom', 
                popup: 'swal-popup-custom', 
                title: 'swal-title-custom', 
                confirmButton: 'swal-delete-button-custom', 
                cancelButton: 'swal-cancel-button-custom' 
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE_URL}/api/veiculos/despesas/${despesaId}`, { 
                        headers: { 'Authorization': `Bearer ${token}` } 
                    });
                    
                    Swal.fire({ 
                        icon: 'success', 
                        title: 'Excluído!', 
                        text: 'A despesa foi removida.', 
                        confirmButtonText: 'OK', 
                        customClass: { 
                            container: 'swal-container-custom', 
                            popup: 'swal-popup-custom', 
                            title: 'swal-title-custom', 
                            confirmButton: 'swal-confirm-button-custom' 
                        } 
                    });
                    
                    fetchAllData();
                } catch (error) {
                    console.error("Erro ao excluir despesa:", error);
                    Swal.fire({ 
                        icon: 'error', 
                        title: 'Erro!', 
                        text: 'Não foi possível excluir a despesa.' 
                    });
                }
            }
        });
    };

    const handleFornecedorSaveSuccess = (novoFornecedor) => {
        setFornecedores(prev => [...prev, { id: novoFornecedor.id, nome_empresa: novoFornecedor.nome_empresa }]);
        setNovaDespesa(prev => ({...prev, fornecedor_id: novoFornecedor.id}));
        setIsFornecedorPopupOpen(false);
    };

    const formatCurrency = (value) => value ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value) : 'R$ 0,00';
    
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    
    const handleSelectItem = (id) => {
        setSelecaoRelatorio(prev => 
            prev.includes(id) 
                ? prev.filter(itemId => itemId !== id) 
                : [...prev, id] 
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelecaoRelatorio(filteredDespesas.map(d => d.id));
        } else {
            setSelecaoRelatorio([]);
        }
    };
    
    const handleGerarRelatorio = async () => {
        const despesasParaRelatorio = despesas.filter(d => selecaoRelatorio.includes(d.id));
        if (despesasParaRelatorio.length === 0) {
            Swal.fire('Atenção', 'Selecione pelo menos uma despesa para gerar o relatório.', 'warning');
            return;
        }
        const descricaoVeiculo = vehicle.placa ? `${vehicle.marca} - ${vehicle.placa}` : vehicle.marca;
        const veiculoParaRelatorio = { descricao: descricaoVeiculo };
        await gerarRelatorioDeSelecaoPDF({ 
            despesasSelecionadas: despesasParaRelatorio, 
            veiculo: veiculoParaRelatorio, 
            logo: logo, 
            chartContainerRef 
        });
    };

    const universalTooltip = {
        callbacks: {
            label: (context) => {
                const label = context.label || '';
                const value = (context.parsed && typeof context.parsed === 'object') ? (context.parsed.y || context.parsed.x) : context.parsed;
                return `${label}: ${formatCurrency(value)}`;
            }
        }
    };

    const pluginsConfig = {
        legend: { 
            position: 'right', 
            labels: { 
                boxWidth: 20, 
                padding: 15, 
                font: { size: 12 } 
            } 
        },
        title: { display: false },
        tooltip: universalTooltip,
        datalabels: { display: false }
    };

    const doughnutOptions = { 
        responsive: true, 
        maintainAspectRatio: false, 
        plugins: pluginsConfig 
    };
    
    const horizontalBarOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { 
            ...pluginsConfig, 
            legend: { display: false } 
        },
        scales: { 
            x: { 
                ticks: { 
                    callback: (value) => formatCurrency(value) 
                } 
            } 
        }
    };
    
    const verticalBarOptions = {
        indexAxis: 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { 
            ...pluginsConfig, 
            legend: { display: false } 
        },
        scales: { 
            y: { 
                ticks: { 
                    callback: (value) => formatCurrency(value) 
                } 
            } 
        }
    };

    return (
        <>
            <div ref={chartContainerRef} style={{ position: 'fixed', left: '-9999px', top: '-9999px' }} />
            <div className={styles.popupOverlay}>
                <div className={styles.popupContent}>
                    <header className={styles.popupHeader}>
                        <div className={styles.headerTitleContainer}>
                            <h2>Gestão de Despesas <span className={styles.subtitle}>{vehicle.marca} {vehicle.modelo}</span></h2>
                        </div>
                        <button onClick={onClose} className={styles.closePopupBtn}>&times;</button>
                    </header>

                    <main className={styles.popupBody}>
                        {!isLoading && (
                            <div className={styles.kpiContainer}>
                                <div className={styles.kpiCard}>
                                    <div className={styles.kpiIcon}><FiDollarSign /></div>
                                    <div>
                                        <div className={styles.kpiValue}>{formatCurrency(kpiData.custoTotal)}</div>
                                        <div className={styles.kpiLabel}>Custo Total (Filtro)</div>
                                    </div>
                                </div>
                                <div className={styles.kpiCard}>
                                    <div className={styles.kpiIcon}><FiTool /></div>
                                    <div>
                                        <div className={styles.kpiValue}>{kpiData.principalDespesa}</div>
                                        <div className={styles.kpiLabel}>Principal Despesa (Filtro)</div>
                                    </div>
                                </div>
                                <div className={styles.kpiCard}>
                                    <div className={styles.kpiIcon}><FaGasPump /></div>
                                    <div>
                                        <div className={styles.kpiValue}>{kpiData.consumoMedio.toFixed(2)} KM/L</div>
                                        <div className={styles.kpiLabel}>Consumo Médio (Filtro)</div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {!isLoading && chartData && (
                            <div className={styles.chartContainer}>
                                <div className={styles.chartHeader}>
                                    <h3 className={styles.chartTitle}>Resumo por Tipo de Despesa (Filtro)</h3>
                                    <div className={styles.chartToggleContainer}>
                                        <button className={`${styles.chartToggleButton} ${chartType === 'doughnut' ? styles.active : ''}`} onClick={() => setChartType('doughnut')} title="Gráfico de Rosca"><FiPieChart /></button>
                                        <button className={`${styles.chartToggleButton} ${chartType === 'horizontalBar' ? styles.active : ''}`} onClick={() => setChartType('horizontalBar')} title="Barras Horizontais"><FiBarChart2 /></button>
                                        <button className={`${styles.chartToggleButton} ${chartType === 'verticalBar' ? styles.active : ''}`} onClick={() => setChartType('verticalBar')} title="Barras Verticais"><FiTrendingUp /></button>
                                    </div>
                                </div>
                                <div className={styles.chartWrapper}>
                                    {chartType === 'doughnut' && <Doughnut data={chartData} options={doughnutOptions} />}
                                    {chartType === 'horizontalBar' && <Bar data={chartData} options={horizontalBarOptions} />}
                                    {chartType === 'verticalBar' && <Bar data={chartData} options={verticalBarOptions} />}
                                </div>
                            </div>
                        )}

                        <form className={styles.formAddDespesa} onSubmit={handleAddDespesa}>
                            <div className={styles.formGroup}>
                                <label htmlFor="data">Data *</label>
                                <input type="date" id="data" name="data" value={novaDespesa.data} onChange={handleInputChange} className={styles.formInput} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="tipo_despesa">Tipo *</label>
                                <select id="tipo_despesa" name="tipo_despesa" value={novaDespesa.tipo_despesa} onChange={handleInputChange} className={styles.formSelect} required>
                                    <option value="">Selecione...</option>
                                    {tiposDespesa.map(tipo => (
                                        <option key={tipo.id} value={tipo.nome}>{tipo.nome}</option>
                                    ))}
                                    <option value="--CRIAR-NOVO--" className={styles.optionAddNew}>
                                        » Adicionar Novo Tipo...
                                    </option>
                                </select>
                            </div>
                            <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                                <label htmlFor="descricao">Descrição *</label>
                                <input type="text" id="descricao" name="descricao" value={novaDespesa.descricao} onChange={handleInputChange} className={styles.formInput} required />
                            </div>
                            <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                                <label>Fornecedor</label>
                                <div className={styles.fornecedorInputWrapper}>
                                    <select id="fornecedor_id" name="fornecedor_id" value={novaDespesa.fornecedor_id} onChange={handleInputChange} className={styles.formSelect}>
                                        <option value="">Selecione um fornecedor...</option>
                                        {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome_empresa}</option>)}
                                    </select>
                                    <button type="button" className={styles.addFornecedorBtn} onClick={() => setIsFornecedorPopupOpen(true)} title="Cadastrar Novo Fornecedor">
                                        <FiUserPlus />
                                    </button>
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="numero_nota_fiscal">Nº Nota Fiscal</label>
                                <input type="text" id="numero_nota_fiscal" name="numero_nota_fiscal" value={novaDespesa.numero_nota_fiscal} onChange={handleInputChange} className={styles.formInput} />
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="quantidade_itens">Quantidade *</label>
                                <input type="text" id="quantidade_itens" name="quantidade_itens" value={novaDespesa.quantidade_itens} onChange={handleNumericInputChange} className={styles.formInput} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="valor_unitario">Valor Unitário (R$) *</label>
                                <input type="text" id="valor_unitario" name="valor_unitario" value={novaDespesa.valor_unitario} onChange={handleNumericInputChange} className={styles.formInput} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Valor Total</label>
                                <input type="text" value={formatCurrency(valorTotalCalculado)} className={styles.formInput} disabled style={{backgroundColor: '#e9ecef', fontWeight: 'bold'}} />
                            </div>
                            <div className={styles.formGroupCheck}>
                                <input type="checkbox" id="showOdometroAdd" className={styles.formCheckInput} checked={showOdometro} onChange={handleOdometroCheckboxChange} />
                                <label htmlFor="showOdometroAdd">Informar Horímetro/KM?</label>
                            </div>
                            {showOdometro && ( 
                                <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                                    <label htmlFor="odometro">Horímetro/KM</label>
                                    <input type="text" id="odometro" name="odometro" value={novaDespesa.odometro} onChange={handleNumericInputChange} className={styles.formInput} />
                                </div> 
                            )}
                            <button type="submit" className={styles.addButton} disabled={isLoading}><FiPlus /> Adicionar</button>
                        </form>
                        
                        <div className={styles.filterContainer}>
                            <div className={styles.filterGroup}>
                                <FiSearch />
                                <input type="text" placeholder="Buscar por descrição..." className={styles.formInput} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                            <div className={styles.filterGroup}>
                                <FiFilter />
                                <select className={styles.formSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
                                    <option value="">Todos os Tipos</option>
                                    {tiposDespesa.map(tipo => (
                                        <option key={tipo.id} value={tipo.nome}>{tipo.nome}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.filterGroup}>
                                <FiCalendar />
                                <input type="date" className={styles.formInput} value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} title="Data de Início"/>
                                <span className={styles.dateSeparator}>até</span>
                                <input type="date" className={styles.formInput} value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} title="Data de Fim"/>
                            </div>
                        </div>
                        
                        {isLoading ? ( 
                            <p>Carregando despesas...</p> 
                        ) : despesas.length > 0 ? (
                            <>
                                <table className={styles.despesasTable}>
                                    <thead>
                                        <tr>
                                            <th className={styles.selectionColumn}><input type="checkbox" className={styles.styledCheckbox} onChange={handleSelectAll} checked={selecaoRelatorio.length > 0 && selecaoRelatorio.length === filteredDespesas.length && filteredDespesas.length > 0} title="Selecionar Todos"/></th>
                                            <th>Data</th>
                                            <th>Descrição</th>
                                            <th>Consumo</th>
                                            <th>Qtd</th>
                                            <th>Vlr. Unit.</th>
                                            <th>Vlr. Total</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itemsDaPaginaAtual.map((item) => (
                                            item.type === 'header' ? ( 
                                                <tr key={item.id} className={styles.monthHeaderRow}>
                                                    <td colSpan="8">{item.title}</td>
                                                </tr> 
                                            ) : (
                                                <tr key={item.id} className={selecaoRelatorio.includes(item.id) ? styles.selectedRow : ''}>
                                                    <td className={styles.selectionColumn}><input type="checkbox" className={styles.styledCheckbox} checked={selecaoRelatorio.includes(item.id)} onChange={() => handleSelectItem(item.id)}/></td>
                                                    <td data-label="Data">{formatDate(item.data)}</td>
                                                    <td data-label="Descrição">
                                                        <div className={styles.descriptionCellContent}>
                                                          <span className={styles.iconWrapper}>{iconMap[item.tipo_despesa] || iconMap['Outros']}</span>
                                                          <span className={styles.truncate} title={item.descricao}>{item.descricao}</span>
                                                        </div>
                                                    </td>
                                                    <td data-label="Consumo">
                                                        {item.tipo_despesa === 'Combustível' && item.consumo_km_l > 0 ?
                                                            <span className={styles.truncate} title={`${item.km_percorrido.toFixed(1)} KM percorridos`}>{item.consumo_km_l.toFixed(2)} KM/L</span>
                                                            : 'N/A'
                                                        }
                                                    </td>
                                                    <td data-label="Qtd">{formatBRLNumber(item.quantidade_itens)}</td>
                                                    <td data-label="Vlr. Unit.">{formatHighPrecisionCurrency(item.valor_unitario)}</td>
                                                    <td data-label="Vlr. Total">{formatCurrency(item.valor)}</td>
                                                    <td>
                                                        <div className={styles.actionsCell}>
                                                            <button onClick={() => { setSelectedDespesa(item); setIsEditPopupOpen(true); }} title="Editar"><FiEdit /></button>
                                                            <button onClick={() => handleDeleteDespesa(item.id)} title="Excluir"><FiTrash2 /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        ))}
                                    </tbody>
                                </table>
                                
                                {totalPages > 1 && ( 
                                    <div className={styles.paginationContainer}>
                                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className={styles.paginationButton}>Anterior</button>
                                        <span className={styles.paginationInfo}>Página {currentPage} de {totalPages}</span>
                                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className={styles.paginationButton}>Próxima</button>
                                    </div> 
                                )}
                            </>
                        ) : ( 
                            <div className={styles.noDespesas}>
                                <FiFileText />
                                <p>Nenhuma despesa registrada para este veículo ainda.</p>
                            </div> 
                        )}
                    </main>

                    <footer className={styles.popupActions}>
                        <button type="button" className={styles.reportButton} onClick={handleGerarRelatorio} disabled={selecaoRelatorio.length === 0}>
                            <FiPrinter /> Gerar Relatório ({selecaoRelatorio.length})
                        </button>
                        <button type="button" className={styles.btnClose} onClick={onClose}>Fechar</button>
                    </footer>
                </div>
            </div>
            {isEditPopupOpen && <EditDespesasPopup 
                despesa={selectedDespesa} 
                fornecedores={fornecedores} 
                onClose={() => setIsEditPopupOpen(false)} 
                onSave={() => { setIsEditPopupOpen(false); fetchAllData(); }} 
                tiposDespesa={tiposDespesa}
                onAddNewTipoDespesa={handleAddNewTipoDespesa}
            />}
            {isFornecedorPopupOpen && <FornecedorPopup onClose={() => setIsFornecedorPopupOpen(false)} onSaveSuccess={handleFornecedorSaveSuccess} />}
        </>
    );
};

export default DespesasPopup;